import { z } from 'zod';
/**
 * Số liệu **toàn hệ thống**, chỉ `SUPER_ADMIN` đọc — APM-SA-a.
 *
 * ## Vì sao nó không giống mọi báo cáo khác trong repo
 *
 * Mọi con số khác đều thuộc về một dự án hoặc một tổ chức, và repository tự
 * chèn phạm vi của người gọi (luật #5). Ở đây phạm vi **là cả cài đặt**: người
 * vận hành cần biết hệ thống có bao nhiêu tổ chức, chúng dùng tới đâu, và hôm
 * nay có ai mới. Không có câu trả lời ấy thì "hệ thống đang chạy ra sao" chỉ
 * đo được bằng cảm giác.
 *
 * ## Vì sao mọi thứ nhận `asOf`
 *
 * Luật #7: view đọc đại lượng biến thiên theo thời gian phải dựng lại được
 * trạng thái ở một mốc quá khứ. Con số tăng trưởng **là** đại lượng như thế, và
 * một bảng "tăng 12 dự án tuần này" không nói được nó tính tới lúc nào là một
 * bảng không đối chiếu được với bất cứ gì.
 */
/** Một đại lượng đếm được, kèm mức tăng trong ba cửa sổ gần nhất. */
export const GrowthCount = z.object({
    /**
     * Còn tồn tại tại `asOf` — tạo trước mốc và chưa bị xoá tính tới mốc đó.
     *
     * Không phải "số hàng trong bảng hôm nay": một tổ chức bị xoá hôm qua **có**
     * tồn tại vào tuần trước, và một bảng lịch sử nói ngược lại thì vô dụng đúng
     * lúc người ta cần nó nhất.
     */
    total: z.number().int().nonnegative(),
    /** Tạo mới trong 24 giờ trước `asOf`. */
    day: z.number().int().nonnegative(),
    /** Bảy ngày trước `asOf`. Bao trùm cả `day`, không trừ ra. */
    week: z.number().int().nonnegative(),
    /** Ba mươi ngày trước `asOf`. Bao trùm cả `week`. */
    month: z.number().int().nonnegative(),
});
/** Số người hoạt động trong ba cửa sổ lùi từ `asOf` (24 giờ, 7 ngày, 30 ngày). */
export const SystemActiveWindows = z.object({
    day: z.number().int().nonnegative(),
    week: z.number().int().nonnegative(),
    month: z.number().int().nonnegative(),
});
/**
 * Người dùng đang hoạt động — APM-SYSMON-a (docs/plan-system-monitor.md §1).
 *
 * "Hoạt động trong X" = có ít nhất một `refresh_token` phát trong X: đăng nhập,
 * hoặc dùng app đủ lâu để access token (15 phút) phải làm mới. Không có cột
 * `last_seen` và cố ý không thêm — đó là một lệnh ghi cho mỗi request.
 *
 * Người đã xoá (tính tới `asOf`) hoặc đang bị khoá không đếm. Phiên trong tổ
 * chức demo tách ra `demo`: tài khoản demo vào không cần mật khẩu, nên số của
 * nó là khách xem thử, không phải khách hàng.
 */
export const SystemActiveUsers = SystemActiveWindows.extend({
    demo: SystemActiveWindows,
});
/**
 * Tệp người dùng tải lên — APM-SYSMON-a (plan §2): `file_object` đã commit và
 * chưa bị xoá tại `asOf`. Tệp do server sinh (ảnh chụp thiết kế…) không tính.
 */
export const SystemFileMetrics = z.object({
    count: GrowthCount,
    /**
     * Byte — số thường chứ không phải chuỗi: `size_bytes` là `BIGINT` nhưng tổng
     * vượt 2^53 (8 PB) thì đã không còn là quy mô của một instance duy nhất.
     */
    bytes: GrowthCount,
});
/** Một tổ chức và mức dùng của nó. */
export const OrgUsageRow = z.object({
    orgId: z.uuid(),
    name: z.string(),
    /** Tổ chức trưng bày — số của nó không nói gì về khách hàng thật. */
    isDemo: z.boolean(),
    projects: z.number().int().nonnegative(),
    users: z.number().int().nonnegative(),
    /** Người có phiên trong tổ chức này trong 30 ngày trước `asOf` — APM-SYSMON-a. */
    activeUsers30d: z.number().int().nonnegative(),
    /** Tệp người dùng tải lên còn tồn tại tại `asOf`, và tổng dung lượng (byte). */
    files: z.number().int().nonnegative(),
    bytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
});
export const SystemMetrics = z.object({
    /** Mốc mà mọi con số dưới đây tính tới. */
    asOf: z.iso.datetime(),
    orgs: GrowthCount,
    users: GrowthCount,
    projects: GrowthCount,
    activeUsers: SystemActiveUsers,
    files: SystemFileMetrics,
    /**
     * Từng tổ chức, nhiều dự án trước.
     *
     * Cắt bớt ở `orgLimit` để một cài đặt có hàng nghìn tổ chức không trả về một
     * phản hồi không ai đọc nổi; `orgsOmitted` nói phần bị cắt là bao nhiêu chứ
     * không im lặng.
     */
    perOrg: z.array(OrgUsageRow),
    orgsOmitted: z.number().int().nonnegative(),
});
//# sourceMappingURL=system-metrics.js.map